import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useProject } from "@/lib/workflow/useProject";
import { applyRsvpAnswers } from "@/lib/workflow/rsvpApply";

const field = "w-full rounded-md border bg-background px-3 py-2 text-sm";
const when = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * Event Suite Premium: RSVP answers and text reminders. Guests choose text on their RSVP page, which is their consent.
 * Texts go out through the organiser's OWN Twilio account and number, so registration, billing and carrier approval are theirs.
 */
export function TextReminders() {
  const [project, store] = useProject();
  const utils = trpc.useUtils();
  const status = trpc.eventSuite.text.status.useQuery({ eventName: project.name }, { retry: false });
  const responses = trpc.eventSuite.text.responses.useQuery({ eventName: project.name }, { retry: false });
  const connect = trpc.eventSuite.text.connect.useMutation();
  const disconnect = trpc.eventSuite.text.disconnect.useMutation();
  const send = trpc.eventSuite.text.send.useMutation();
  const cancel = trpc.eventSuite.text.cancel.useMutation();

  const [form, setForm] = useState({ accountSid: "", apiKeySid: "", apiKeySecret: "", phone: "" });
  const [text, setText] = useState("Dear {name}, a reminder that our event is coming up. We look forward to seeing you.");
  const [zone, setZone] = useState("us");
  const [later, setLater] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const d = status.data;
  const preview = useMemo(() => `${text.replace(/\{name\}/gi, "Ann")} Reply STOP to opt out.`, [text]);
  if (status.isError) return null;
  if (!d) return null;

  const apply = () => {
    const r = applyRsvpAnswers(project.data.guests, (responses.data ?? []).map((x) => ({ email: x.email, status: x.status })));
    if (r.changed) store.setRecords("guests", r.guests);
    toast.success(r.changed ? `Updated ${r.changed} guest${r.changed === 1 ? "" : "s"} in your guest list.` : "Your guest list already matches their answers.");
  };

  const doConnect = async () => {
    try {
      const r = await connect.mutateAsync(form);
      toast.success(`Connected ${r.phone}.`);
      setForm({ accountSid: "", apiKeySid: "", apiKeySecret: "", phone: "" });
      await utils.eventSuite.text.status.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not connect. Please try again.");
    }
  };
  const doDisconnect = async () => {
    if (!window.confirm("Disconnect your Twilio number? We delete your stored key and cancel waiting texts.")) return;
    await disconnect.mutateAsync();
    await utils.eventSuite.text.status.invalidate();
    toast.success("Disconnected. Your key has been deleted from FreeDailyPro.");
  };
  const doSend = async () => {
    try {
      const r = await send.mutateAsync({ eventName: project.name, text, zone, sendAt: later && sendAt ? new Date(sendAt).toISOString() : null });
      toast.success(r.scheduled ? `Scheduled for ${r.count} guest${r.count === 1 ? "" : "s"}.` : `Sending to ${r.count} guest${r.count === 1 ? "" : "s"}. It starts within a minute and takes about a second per guest.`);
      await utils.eventSuite.text.status.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send. Please try again.");
    }
  };

  const canSend = !!d.connection && d.counts.byText > 0 && text.trim().length >= 5 && text.length <= d.maxChars && (!later || !!sendAt) && !send.isPending;

  return (
    <section className="mt-3 rounded-lg border bg-card p-4 text-sm" data-testid="text-reminders">
      <h3 className="flex items-center gap-2 font-semibold"><MessageSquare className="h-4 w-4" aria-hidden />RSVP answers and text reminders</h3>

      <p className="mt-2 text-xs text-muted-foreground" data-testid="rsvp-counts">
        {d.counts.invited === 0
          ? "Send an invitation with the RSVP link switched on. Guests' answers appear here."
          : `${d.counts.invited} invited: ${d.counts.accepted} coming, ${d.counts.declined} not coming, ${d.counts.waiting} not answered. ${d.counts.byText} chose text reminders and ${d.counts.byEmail} chose email.`}
      </p>
      {d.counts.invited > 0 && <Button size="sm" variant="outline" className="mt-2" onClick={apply} data-testid="rsvp-apply">Update my guest list from their answers</Button>}

      <div className="mt-4 border-t pt-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your Twilio number</h4>
        {!d.ready ? (
          <p className="mt-1 text-xs text-muted-foreground">Text reminders are not switched on yet. Email reminders work now.</p>
        ) : d.connection ? (
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <span>Connected: <strong>{d.connection.phone}</strong> (account ending {d.connection.accountEnd})</span>
            <Button size="sm" variant="ghost" onClick={() => void doDisconnect()} data-testid="text-disconnect">Disconnect</Button>
          </div>
        ) : (
          <div className="mt-1">
            <p className="text-xs text-muted-foreground">Texts are sent from your own Twilio number, so Twilio bills you and approves your messages. FreeDailyPro never sees your Twilio password or Auth Token.</p>
            <p className="text-xs text-muted-foreground">You are the sender: only invite people who expect to hear from you. Guests choose text themselves on the RSVP page and can reply STOP at any time. See the Terms of Service for details.</p>
            <button type="button" className="mt-1 text-xs underline" onClick={() => setShowHelp(!showHelp)}>{showHelp ? "Hide" : "Show"} the setup steps</button>
            {showHelp && (
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-muted-foreground" data-testid="text-help">
                <li>Create an account at twilio.com and buy a phone number that can send text messages.</li>
                <li>Register that number for US messaging in Twilio (called A2P 10DLC, or toll-free verification). Twilio walks you through it. Until it is approved, US carriers may block your texts.</li>
                <li>In the Twilio Console, create an API key of the <strong>Standard</strong> type. Copy its SID (starts with SK) and its secret.</li>
                <li>Copy your Account SID (starts with AC) from the Console home page. Do <strong>not</strong> use your Auth Token.</li>
                <li>Paste the four details below. We keep the secret encrypted, only to send your texts, and delete it when you disconnect or your plan ends.</li>
              </ol>
            )}
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <input className={field} placeholder="Account SID (AC...)" value={form.accountSid} onChange={(e) => setForm({ ...form, accountSid: e.target.value })} autoComplete="off" data-testid="twilio-account" />
              <input className={field} placeholder="Twilio phone number, +1 555 123 4567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} autoComplete="off" data-testid="twilio-phone" />
              <input className={field} placeholder="API key SID (SK...)" value={form.apiKeySid} onChange={(e) => setForm({ ...form, apiKeySid: e.target.value })} autoComplete="off" data-testid="twilio-key" />
              <input className={field} type="password" placeholder="API key secret" value={form.apiKeySecret} onChange={(e) => setForm({ ...form, apiKeySecret: e.target.value })} autoComplete="new-password" data-testid="twilio-secret" />
            </div>
            <Button size="sm" className="mt-2" onClick={() => void doConnect()} disabled={connect.isPending || !form.accountSid || !form.apiKeySid || !form.apiKeySecret || !form.phone} data-testid="twilio-connect">
              {connect.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Connect my number
            </Button>
          </div>
        )}
      </div>

      {d.connection && (
        <div className="mt-4 border-t pt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Text the {d.counts.byText} guest{d.counts.byText === 1 ? "" : "s"} who chose text</h4>
          <textarea className={`${field} mt-1 min-h-[80px]`} value={text} maxLength={d.maxChars + 200} onChange={(e) => setText(e.target.value)} data-testid="text-body" />
          <p className="mt-1 text-xs text-muted-foreground" data-testid="text-preview">
            {text.length}/{d.maxChars} characters. {"{name}"} becomes the first name. Guests see: {preview}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs">Guests' time zone
              <select className="rounded-md border bg-background px-2 py-1" value={zone} onChange={(e) => setZone(e.target.value)} data-testid="text-zone">
                {d.zones.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2"><input type="radio" checked={!later} onChange={() => setLater(false)} />Send now</label>
            <label className="flex items-center gap-2"><input type="radio" checked={later} onChange={() => setLater(true)} />Send later</label>
            {later && <input type="datetime-local" className="rounded-md border bg-background px-2 py-1" value={sendAt} onChange={(e) => setSendAt(e.target.value)} data-testid="text-send-at" />}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Texts only go out between 8am and 9pm for your guests. {d.sentLast24h} of {d.dailyLimit} texts used in the last 24 hours. Only guests who agreed on their RSVP page are texted, and anyone who replies STOP is never texted again.</p>
          <Button size="sm" className="mt-2" onClick={() => void doSend()} disabled={!canSend} data-testid="text-send">
            {send.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5 mr-1" />}
            {later ? "Schedule" : "Send"} to {d.counts.byText} guest{d.counts.byText === 1 ? "" : "s"}
          </Button>

          {d.scheduled.length > 0 && (
            <ul className="mt-3 divide-y">
              {d.scheduled.map((s) => (
                <li key={s.id} className="flex items-center gap-2 py-1.5">
                  <span className="min-w-0 flex-1 truncate">{s.recipientCount} guests, {when(s.sendAt)}</span>
                  <Button size="sm" variant="ghost" aria-label="Cancel this text" onClick={async () => { await cancel.mutateAsync({ id: s.id }); await utils.eventSuite.text.status.invalidate(); }}><X className="h-3.5 w-3.5" /></Button>
                </li>
              ))}
            </ul>
          )}
          {d.recent.length > 0 && (
            <ul className="mt-3 text-xs text-muted-foreground">
              {d.recent.map((r) => <li key={r.id}>{r.status === "sending" ? "Sending now" : r.status === "sent" ? `${r.sentCount} sent${r.failedCount ? `, ${r.failedCount} could not be sent` : ""}` : "Could not be sent"}, {when(r.at)}</li>)}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Guests' replies are emailed to you. Delivery details are in your Twilio console.</p>
        </div>
      )}
    </section>
  );
}
